import {
  IContextMenuItemRefreshRulesForDatasets,
  IContextMenuItemRefreshRulesForWholeTree,
  IInParamsValues,
  tg,
} from '@infotech-soe/metaschema';
import { isEqual } from 'lodash';

import { AppDispatch } from '../..';
import { DatasetParamValues } from '../../../infrastructure/providers/abstract/IApiProvider';
import {
  CategoryOpenMode,
  CategoryStateEntity,
} from '../../../models/category/category';
import { HookTypes } from '../../../models/category/HookTypes';
import CategoryConditionsService from '../../../services/category/CategoryConditionsService';
import CategoryService from '../../../services/category/CategoryService';
import ICategoryConditionsService from '../../../services/category/ICategoryConditionsService';
import ICategoryService from '../../../services/category/ICategoryService';
import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';
import { rememberLastReport } from '../../../services/commands/reportSelectionCache';
import UniqueId from '../../../services/uid/UniqueId';
import IUserConfigService from '../../../services/user-config/IUserConfigService';
import UserConfigService from '../../../services/user-config/UserConfigService';
import { AppState } from '../../reducers/root';
import {
  getAllChildrenDatasetsByLayoutElement,
  getCategoryMasterDataset,
  getCategoryOpenMode,
  getCategoryState,
  getPendingLoadConfig,
  isDatasetVisible,
} from '../../selectors/category';
import {
  getDatasetActiveRowId,
  getDatasetCategoryId,
  getDatasetConfigCurrentPreset,
  getDatasetIdByName,
  getDatasetIdFields,
  isDatasetOutdated,
} from '../../selectors/dataset';
import { getControlValue } from '../../selectors/form';
import { getHierarchyStateEntity } from '../../selectors/hierarchy';
import { getSessionApplication } from '../../selectors/session';
import actions from '../../slices/actions';
import balance from '../../slices/balance';
import catalogs from '../../slices/catalogs';
import categoryActions, { CategoryTargetingInfo } from '../../slices/category';
import categoryHook from '../../slices/categoryHook';
import commands from '../../slices/commands';
import contentActions, { ContentEntityType } from '../../slices/content';
import dataset, { DatasetSwitchEffect } from '../../slices/dataset';
import dictionary, {
  DictionaryCloseReason,
  DictionaryType,
} from '../../slices/dictionary';
import hierarchy from '../../slices/hierarchy';
import { RefreshRulesActionCollection } from '../../utils/RefreshRulesActionCollection';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const categoryListener = createAppListener();

const userConfig: IUserConfigService = new UserConfigService();
const conditionsService: ICategoryConditionsService =
  new CategoryConditionsService();
const categoryService: ICategoryService = new CategoryService();

const isInParamsEmpty = (values: IInParamsValues): boolean =>
  Object.values(values).every(
    val => tg.isNothing(val) || tg.isEmptyString(val)
  );

const closeDisplayMode = async (
  categoryState: CategoryStateEntity,
  save = true,
  reason = DictionaryCloseReason.COMMON,
  state: AppState,
  dispatch: AppDispatch
): Promise<void> => {
  const { category } = categoryState;

  if (save) {
    await categoryService.saveCategory(categoryState, state.dataset);
  }
  const { displayModeHooks } = category.source;
  if (displayModeHooks) {
    const rootDataset = getCategoryMasterDataset(state, category.id);
    const hookAction =
      categoryState.openMode === CategoryOpenMode.CONTENT && save
        ? displayModeHooks.close?.action
        : categoryState.openMode === CategoryOpenMode.DICTIONARY &&
            reason === DictionaryCloseReason.CANCEL
          ? displayModeHooks.cancel?.action
          : undefined;

    if (hookAction) {
      dispatch(
        commands.execute({
          datasetId: rootDataset.id,
          command: hookAction,
          args: {},
          formId: undefined,
          hook: HookTypes.CLOSE,
        })
      );
    }
  }

  if (category.treeType === CategoryTreeType.CATALOGS) {
    dispatch(catalogs.destroyTree({ catalogId: category.id }));
  }

  if (category.treeType === CategoryTreeType.HIERARCHY) {
    dispatch(hierarchy.destroyTree({ categoryId: category.id }));
  }
};

categoryListener.startListening(
  apiCall(
    {
      request: categoryActions.openDisplayMode,
      success: categoryActions.openDisplayModeSuccess,
      failure: categoryActions.openDisplayModeFailure,
    },
    async (
      {
        category,
        displayMode,
        inParams,
        nextAction,
        ui,
        withDefaultConfig,
        pinned,
      },
      state
    ) => {
      const hideTree =
        ui.mode === CategoryOpenMode.DICTIONARY ? ui.hideTree : undefined;
      const hideCheckbox =
        ui.mode === CategoryOpenMode.DICTIONARY ? ui.hideCheckbox : undefined;
      const displayModeName =
        displayMode ??
        (await categoryService.getDisplayMode(
          getSessionApplication(state),
          category
        ));

      const displayModeConfig = await userConfig.getDisplayModeConfig(
        category,
        displayModeName,
        withDefaultConfig
      );

      const result = await categoryService.openDisplayMode(
        category,
        displayModeName,
        inParams,
        hideTree,
        hideCheckbox,
        displayModeConfig.layoutConfig,
        withDefaultConfig
      );

      if (displayModeConfig.lastReportCode) {
        rememberLastReport(
          category,
          displayModeName,
          displayModeConfig.lastReportCode
        );
      }

      const storedConditions = await userConfig.getConditionsConfig(
        category,
        `${displayMode}_${ui.mode}`,
        displayModeName,
        withDefaultConfig
      );

      result.source.displayModeConditions =
        await conditionsService.mergeConditions(
          category,
          displayModeName,
          storedConditions,
          inParams
        );

      const inParamsValues = { ...inParams };
      result.source.inParams = inParamsValues;

      return {
        result,
        inParams: inParamsValues,
        displayMode: displayModeName,
        displayModeConfig,
        ui,
        nextAction,
        pinned,
      };
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.openDisplayModeSuccess,
    (
      {
        displayMode,
        displayModeConfig,
        inParams,
        nextAction,
        result: { id, title, datasets, treeType },
        ui,
        pinned,
      },
      state,
      dispatch
    ) => {
      if (treeType === CategoryTreeType.CATALOGS) {
        dispatch(catalogs.initializeTree({ catalogId: id }));
      } else if (treeType === CategoryTreeType.HIERARCHY) {
        dispatch(hierarchy.initializeTree({ categoryId: id }));
      } else if (!Object.values(CategoryTreeType).includes(treeType)) {
        throw new Error(`Unsupported type of category's tree: ${treeType}`);
      }

      if (ui.mode !== CategoryOpenMode.TREE) {
        dispatch(
          dataset.createBatch({
            categoryId: id,
            datasets,
            displayModeConfig,
            displayMode,
            inParams,
            pinned,
          })
        );
      } else {
        dispatch(
          categoryActions.load({
            categoryId: id,
            config: {
              treeValue: getControlValue(
                state,
                ui.effect.form,
                ui.effect.field
              ),
            },
          })
        );
      }

      switch (ui.mode) {
        case CategoryOpenMode.CONTENT:
          dispatch(
            contentActions.addEntity({
              type: ContentEntityType.CATEGORY,
              content: id,
              title: title + (inParams?.linksGraph?.title ?? ''),
              pinned,
            })
          );
          break;

        case CategoryOpenMode.HIDDEN: {
          if (nextAction) {
            const rootDataset = Object.values(datasets).find(
              ({ parent }) => parent === null
            );
            if (rootDataset) {
              dispatch(
                commands.initiateScheduled({
                  datasetId: rootDataset.id,
                  pending: [nextAction],
                })
              );
            }
          }
          break;
        }

        case CategoryOpenMode.DICTIONARY:
          dispatch(
            dictionary.open({
              id,
              title,
              effect: ui.effect,
              multiSelect: ui.multiSelect,
              hideTree: ui.hideTree,
              fullHierarchy: ui.fullHierarchy,
              hideCheckbox: ui.hideCheckbox,
              type: DictionaryType.CATEGORY,
            })
          );
          break;

        case CategoryOpenMode.TREE:
          dispatch(
            dictionary.open({
              id,
              effect: ui.effect,
              type: DictionaryType.TREE,
            })
          );
          break;

        case CategoryOpenMode.BALANCE: {
          const targetDataset =
            Object.values(datasets).find(({ name }) => name === ui.category) ??
            Object.values(datasets).find(({ parent }) => parent !== null);
          if (targetDataset?.id) {
            dispatch(
              balance.setDataset({
                id: ui.balanceId,
                datasetId: targetDataset.id,
              })
            );
          }
          break;
        }
      }
    }
  )
);

categoryListener.startListening(
  postDispatch(
    dataset.createBatchSuccess,
    async (
      {
        categoryId,
        displayMode,
        inParams,
        treeActiveId,
        treeCheckedIds,
        pinned,
      },
      state,
      dispatch
    ) => {
      const { id, source } = getCategoryState(state, categoryId);
      const rootDataset = getCategoryMasterDataset(state, id);
      const openMode = getCategoryOpenMode(state, categoryId);
      const { showDictionaryCondition } = getDatasetConfigCurrentPreset(
        state,
        rootDataset.id
      );

      const openHook = source.displayModeHooks?.open;
      const isDictionaryConditionsHook =
        openHook === 'conditions' &&
        openMode === CategoryOpenMode.DICTIONARY &&
        showDictionaryCondition;
      const hasConditionsInInParams =
        isDictionaryConditionsHook &&
        (await conditionsService.hasConditionInParams(
          source.category,
          source.displayMode,
          inParams
        ));
      const shouldSkipConditionsHook =
        openHook === 'conditions' &&
        (inParams?.linksGraph ||
          (openMode === CategoryOpenMode.DICTIONARY &&
            (!showDictionaryCondition || hasConditionsInInParams)));

      if (openHook && !shouldSkipConditionsHook && !pinned) {
        const command = openHook === 'conditions' ? 'filter' : openHook.action;

        dispatch(
          categoryActions.scheduleLoading({
            categoryId: id,
            config: { inParams, displayMode, treeActiveId, treeCheckedIds },
          })
        );

        dispatch(
          commands.initiate({
            datasetId: rootDataset.id,
            command,
            hook: HookTypes.OPEN,
          })
        );
      } else {
        dispatch(
          categoryActions.load({
            categoryId,
            config: { inParams, displayMode, treeActiveId, treeCheckedIds },
          })
        );
      }
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.load,
    ({ categoryId, config }, state, dispatch, prevState) => {
      const {
        treeType,
        id,
        source: { category, displayMode },
      } = getCategoryState(prevState, categoryId);
      const openMode = getCategoryOpenMode(prevState, categoryId);

      const loadTree = (
        tree: CategoryTreeType,
        treeCategoryId: UniqueId,
        params?: DatasetParamValues,
        targetingInfo?: CategoryTargetingInfo
      ) => {
        if (tree === CategoryTreeType.HIERARCHY) {
          dispatch(
            hierarchy.loadRoots({
              categoryId: treeCategoryId,
              params,
              targetingInfo,
              forceDOMFocus: true,
            })
          );
        } else if (tree === CategoryTreeType.CATALOGS) {
          dispatch(
            catalogs.loadRoots({
              catalogId: treeCategoryId,
              targetingInfo,
              forceDOMFocus: true,
            })
          );
        }
      };

      if (openMode === CategoryOpenMode.HIDDEN) {
        return;
      } else if (openMode === CategoryOpenMode.TREE) {
        const targeting = config?.treeValue
          ? { treePos: { category, displayMode, value: config.treeValue } }
          : undefined;

        loadTree(treeType, id, {}, targeting);
      } else if (
        [CategoryTreeType.CATALOGS, CategoryTreeType.HIERARCHY].includes(
          treeType
        )
      ) {
        const { inParams, treeActiveId, treeCheckedIds } =
          config || getPendingLoadConfig(prevState, categoryId) || {};

        if (treeCheckedIds) {
          dispatch(
            categoryActions.scheduleLoading({
              categoryId: id,
              config: { treeCheckedIds },
            })
          );
        }

        if (inParams && displayMode) {
          dispatch(
            categoryActions.tracePositions({
              categoryId: id,
              displayMode,
              inParams,
              treeActiveId,
              forceDOMFocus: true,
            })
          );
        } else {
          loadTree(treeType, id);
        }
      } else {
        const categoryState = getCategoryState(state, categoryId);
        const rootDataset = Object.values(categoryState.datasets).find(
          ({ parent }) => parent === null
        );
        if (rootDataset) {
          dispatch(
            dataset.update({
              datasetId: rootDataset.id,
              forceDOMFocus: true,
            })
          );
        }
      }
    }
  )
);

categoryListener.startListening(
  apiCall(
    {
      request: categoryActions.tracePositions,
      success: categoryActions.tracePositionsSuccess,
      failure: categoryActions.tracePositionsFailure,
    },
    async (
      { categoryId, displayMode, inParams, treeActiveId, forceDOMFocus },
      state
    ) => {
      if (
        (isInParamsEmpty(inParams) && tg.isNothing(treeActiveId)) ||
        inParams.linksGraph
      ) {
        return { forceDOMFocus, categoryId };
      }

      const {
        source: { category },
      } = getCategoryState(state, categoryId);

      const results = await categoryService.getItemFromPos(
        category,
        displayMode,
        inParams
      );

      const parentDataset = getCategoryMasterDataset(state, categoryId);
      const entity = results[parentDataset.name];
      const { rowIdField, treeField } = getDatasetIdFields(
        state,
        parentDataset.id
      );
      const relatedWithTree = entity
        ? { entityId: entity[rowIdField], treeId: entity[treeField ?? ''] }
        : treeActiveId
          ? { entityId: 0, treeId: treeActiveId }
          : undefined;

      const datasetIds = Object.entries(results).reduce(
        (memo, [datasetName, currentEntity]) => {
          const datasetId = getDatasetIdByName(state, datasetName, categoryId);
          const { rowIdField: currentEntityRowIdField } = getDatasetIdFields(
            state,
            datasetId
          );

          return {
            ...memo,
            [datasetId]: (currentEntity as any)[currentEntityRowIdField],
          };
        },
        {} as Record<UniqueId, number>
      );

      return {
        forceDOMFocus,
        categoryId,
        targetingInfo: { datasetIds, relatedWithTree },
      };
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.tracePositionsSuccess,
    async ({ categoryId, targetingInfo, forceDOMFocus }, state, dispatch) => {
      const {
        treeType,
        source: { category, displayMode, displayModeConditions, inParams },
      } = getCategoryState(state, categoryId);

      if (treeType === CategoryTreeType.HIERARCHY) {
        const params = await conditionsService.extractTreeParams(
          category,
          displayMode,
          displayModeConditions,
          inParams
        );
        dispatch(
          hierarchy.loadRoots({
            categoryId,
            params,
            targetingInfo,
            forceDOMFocus,
          })
        );
      } else if (treeType === CategoryTreeType.CATALOGS) {
        dispatch(
          catalogs.loadRoots({
            catalogId: categoryId,
            targetingInfo,
            forceDOMFocus,
          })
        );
      }
    }
  )
);

categoryListener.startListening(
  apiCall(
    {
      request: categoryActions.close,
      success: categoryActions.closeSuccess,
      failure: categoryActions.closeFailure,
    },
    async ({ categoryId, save = true, reason }, state, dispatch) => {
      const categoryState = state.category[categoryId];
      const {
        category: { datasets },
      } = categoryState;

      await closeDisplayMode(categoryState, save, reason, state, dispatch);

      for (const datasetId of Object.keys(datasets)) {
        dispatch(dataset.dispose({ datasetId }));
      }

      dispatch(actions.clearProcessState({ categoryId }));

      return { categoryId, save, reason };
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.refreshWithRules,
    async ({ datasetId, refreshRules, rowId }, state, dispatch) => {
      const categoryId = getDatasetCategoryId(state, datasetId);

      if (rowId) {
        dispatch(
          dataset.changeActiveRow({
            datasetId,
            rowId,
            effect: DatasetSwitchEffect.NOTHING,
          })
        );
      }

      if ((refreshRules as IContextMenuItemRefreshRulesForWholeTree).tree) {
        const {
          treeType,
          source: { category, displayMode, displayModeConditions, inParams },
        } = getCategoryState(state, categoryId);

        if (treeType === CategoryTreeType.HIERARCHY) {
          const params = await conditionsService.extractTreeParams(
            category,
            displayMode,
            displayModeConditions,
            inParams
          );
          dispatch(categoryActions.refreshTree({ categoryId, params }));
        } else {
          dispatch(categoryActions.refreshTree({ categoryId }));
        }
      } else {
        const { datasets } = getCategoryState(state, categoryId);

        const mapOfActions = new RefreshRulesActionCollection<
          | ReturnType<typeof dataset.update>
          | ReturnType<typeof dataset.updateRows>
        >(Object.values(datasets));

        for (const [datasetName, rule] of Object.entries(
          (refreshRules as IContextMenuItemRefreshRulesForDatasets).datasets
        )) {
          const currentDatasetId = getDatasetIdByName(
            state,
            datasetName,
            categoryId
          );

          if (!currentDatasetId) {
            continue;
          }

          switch (rule) {
            case 'All':
              mapOfActions.add(
                currentDatasetId,
                dataset.update({ datasetId: currentDatasetId })
              );
              break;
            case 'Current': {
              const currentActiveRow = getDatasetActiveRowId(
                state,
                currentDatasetId
              );

              if (!currentActiveRow) {
                continue;
              }

              mapOfActions.add(
                currentDatasetId,
                dataset.updateRows({
                  datasetId: currentDatasetId,
                  ids: [currentActiveRow],
                })
              );
              break;
            }
            case 'None':
              break;
            default:
              throw new Error(`unknown refresh rule: ${rule}`);
          }
        }

        for (const currentAction of mapOfActions) {
          if (currentAction) {
            dispatch(currentAction);
          }
        }
      }
    }
  )
);

categoryListener.startListening(
  postDispatch(categoryHook.cancel, (source, state, dispatch) => {
    if (!source.dataset) {
      throw new Error(`source is not connected to any dataset`);
    }

    const categoryId = getDatasetCategoryId(state, source.dataset);
    const openMode = getCategoryOpenMode(state, categoryId);

    if (openMode === CategoryOpenMode.CONTENT) {
      const entity = Object.values(state.content.entities).find(
        ({ content, type }) => content === categoryId && type === 'category'
      );
      if (entity) dispatch(contentActions.close({ id: entity.id }));
    } else if (openMode === CategoryOpenMode.DICTIONARY) {
      dispatch(dictionary.close({ id: categoryId }));
    }
  })
);

categoryListener.startListening(
  postDispatch(
    categoryActions.refreshTree,
    ({ categoryId, params }, _state, dispatch, prevState) => {
      const { treeType } = getCategoryState(prevState, categoryId);

      if (treeType === CategoryTreeType.HIERARCHY) {
        dispatch(hierarchy.refresh({ categoryId, params }));
      } else if (treeType === CategoryTreeType.CATALOGS) {
        dispatch(catalogs.refresh({ catalogId: categoryId }));
      } else if (treeType === CategoryTreeType.NONE) {
        return;
      } else {
        throw new Error(
          `Unsupported refresh for such type of category's tree: ${treeType}`
        );
      }
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.updateInParams,
    async ({ categoryId, inParams }, state, dispatch) => {
      const {
        source: { category, displayMode, displayModeConditions },
      } = getCategoryState(state, categoryId);
      const mergedConditions = await conditionsService.mergeConditions(
        category,
        displayMode,
        displayModeConditions,
        inParams
      );

      for (const [datasetName, datasetQueryParams] of Object.entries(
        mergedConditions
      )) {
        if (
          !isEqual(
            datasetQueryParams.conditions,
            displayModeConditions[datasetName].conditions
          )
        ) {
          dispatch(
            categoryActions.updateLocalConditions({
              categoryId,
              datasetName,
              conditions: datasetQueryParams,
            })
          );
        }
      }
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.updateLocalConditions,
    async ({ categoryId, conditions, datasetName }, state, dispatch) => {
      const {
        source: { category, displayMode, inParams },
        treeType,
      } = getCategoryState(state, categoryId);

      if (treeType === CategoryTreeType.HIERARCHY) {
        const { params } = getHierarchyStateEntity(state, categoryId);
        const treeParams = await conditionsService.mergeTreeParams(
          category,
          displayMode,
          datasetName,
          params,
          conditions.conditions,
          inParams
        );

        if (!isEqual(params, treeParams)) {
          dispatch(
            categoryActions.refreshTree({ categoryId, params: treeParams })
          );
        }
      }
    }
  )
);

categoryListener.startListening(
  postDispatch(
    categoryActions.activateLayoutTab,
    ({ categoryId, tabsElementId }, state, dispatch) => {
      const datasets = getAllChildrenDatasetsByLayoutElement(
        state,
        categoryId,
        tabsElementId
      );

      for (const { dataset: datasetId } of datasets) {
        if (
          !isDatasetOutdated(state, datasetId) ||
          !isDatasetVisible(state, datasetId)
        ) {
          continue;
        }

        dispatch(dataset.update({ datasetId }));
      }
    }
  )
);

export default categoryListener.middleware;
